import {
  MIN_PASSWORD_LENGTH,
  PASSWORD_STRENGTH_LABELS,
  checkInvitationPassword,
  passwordStrength,
} from "@xop-any-time/shared";
import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { JSX } from "react";

/**
 * The live read-out under the password field: three strength segments and
 * one line of guidance.
 *
 * It exists because the alternative — submit, get refused, guess again — is
 * how password rules are usually taught, and it is a bad way to teach them.
 * The rule (`checkInvitationPassword`, the same function the server
 * enforces) is applied on every keystroke, so the screen can say what is
 * wrong while it is still cheap to fix.
 *
 * It says ONE thing at a time. A checklist of five simultaneous
 * requirements reads as a wall and gets skimmed; the single next obstacle
 * gets read. `checkInvitationPassword` already returns problems in the
 * order worth fixing them.
 *
 * The meter is deliberately coarse — three steps, no percentage, no
 * bits-of-entropy figure. A precise-looking number the maths does not
 * support is worse than an honest vague one.
 */
export function PasswordCoach({
  password,
  email,
}: {
  password: string;
  email: string;
}): JSX.Element | null {
  // Nothing typed yet: the field's own hint is doing the work, and an empty
  // meter would just be furniture.
  if (password === "") return null;

  const verdict = checkInvitationPassword(password, email);
  const score = passwordStrength(password, email);
  const label = PASSWORD_STRENGTH_LABELS[score];

  return (
    <Box>
      {/* Held clear of the field's bottom edge. Flush against it, the
          segments read as an underline belonging to the input rather than
          as a meter of their own. */}
      <Box sx={{ display: "flex", gap: 0.75, alignItems: "center", mt: 0.25 }} aria-hidden>
        {[1, 2, 3].map((step) => (
          <Box
            key={step}
            sx={{
              height: 3,
              flex: 1,
              borderRadius: 999,
              bgcolor: (t) =>
                score >= step
                  ? score === 1
                    ? t.palette.warning.main
                    : t.palette.accent[500]
                  : alpha(t.palette.text.primary, 0.12),
              transition: "background-color 220ms cubic-bezier(0.22, 1, 0.36, 1)",
            }}
          />
        ))}
      </Box>

      {/* One live region for both the meter's word and the guidance, so a
          screen reader hears the verdict rather than a stream of updates for
          every keystroke. `polite` because the person is still typing. */}
      <Typography
        component="p"
        role="status"
        aria-live="polite"
        sx={{
          mt: 0.9,
          fontSize: 12,
          lineHeight: 1.5,
          color: (t) => (verdict.ok ? t.palette.text.secondary : t.palette.text.primary),
        }}
      >
        {verdict.ok ? (
          <>
            <Box
              component="span"
              sx={{ fontWeight: 600, color: (t) => t.palette.accent[500] }}
            >
              {label}
            </Box>
            {score < 3 ? " — a longer passphrase is stronger still." : " — that'll do nicely."}
          </>
        ) : (
          verdict.message
        )}
      </Typography>
    </Box>
  );
}

/** The one-line hint under an empty field: the rule, stated before it can be
 * broken rather than after. */
export const PASSWORD_HINT = `At least ${MIN_PASSWORD_LENGTH} characters. A few unrelated words beat a short jumble.`;
